import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { localStorageKeys } from '@/utils/tokens';
import { saveFilterToLocalStorage } from '@/utils/localStorage';
import { SortDirection } from '@/utils/sortingUtils';

const ALLOWED_FILTERS = ['sortOptions'];

export type JourneySortField =
  | 'select'
  | 'contract'
  | 'scheduledDate'
  | 'blockExternalId'
  | 'startTime'
  | 'lineNumber'
  | 'from.stopPlaceName'
  | 'endTime'
  | 'to.stopPlaceName';

export interface SortOption {
  field: JourneySortField;
  direction: SortDirection;
}

export interface JourneySortingState {
  sortOptions: SortOption[];
}

export const defaultJourneySorting: JourneySortingState = {
  sortOptions: [],
};

const initialState: JourneySortingState = {
  ...defaultJourneySorting,
  ...JSON.parse(localStorage.getItem(localStorageKeys.journeySorting) || '{}'),
};

const journeySortingSlice = createSlice({
  name: 'journeySorting',
  initialState,
  reducers: {
    setSortOptions(
      state,
      action: PayloadAction<{
        field: JourneySortField;
        direction?: SortDirection;
      }>
    ) {
      const { field, direction } = action.payload;

      const existingIndex = state.sortOptions.findIndex(
        (opt) => opt.field === field
      );

      let newDirection: SortDirection;
      if (
        existingIndex !== -1 &&
        !direction &&
        state.sortOptions[existingIndex]
      ) {
        newDirection =
          state.sortOptions[existingIndex].direction === 'asc' ? 'desc' : 'asc';
      } else {
        newDirection = direction || 'asc';
      }

      if (existingIndex !== -1) {
        state.sortOptions.splice(existingIndex, 1);
      }

      state.sortOptions.push({ field, direction: newDirection });

      saveFilterToLocalStorage(
        localStorageKeys.journeySorting,
        state,
        ALLOWED_FILTERS
      );
    },
    removeSortField(state, action: PayloadAction<{ field: JourneySortField }>) {
      const { field } = action.payload;

      const existingIndex = state.sortOptions.findIndex(
        (opt) => opt.field === field
      );

      if (existingIndex !== -1) {
        state.sortOptions.splice(existingIndex, 1);

        saveFilterToLocalStorage(
          localStorageKeys.journeySorting,
          state,
          ALLOWED_FILTERS
        );
      }
    },
    resetSortOptions(state) {
      state.sortOptions = [...defaultJourneySorting.sortOptions];
      saveFilterToLocalStorage(
        localStorageKeys.journeySorting,
        state,
        ALLOWED_FILTERS
      );
    },
  },
});

export const { setSortOptions, removeSortField, resetSortOptions } =
  journeySortingSlice.actions;

export default journeySortingSlice.reducer;
